<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import SidebarNotificationBell from 'dashboard/components-next/sidebar/SidebarNotificationBell.vue';
import { findRailSectionForRoute } from 'dashboard/components-next/rail/sections';
import { isAInboxViewRoute } from 'dashboard/helper/routeHelpers';
import { useSidebarMode } from 'dashboard/components-next/rail/useSidebarMode';
import { useSetupSteps } from 'dashboard/routes/dashboard/setup/useSetupSteps';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isMini, hasPanel, toggleMode } = useSidebarMode();

// The setup chip: administrators see remaining setup work from any screen and
// the chip disappears once the workspace is fully set up.
const currentRole = useMapGetter('getCurrentRole');
const { steps, doneCount, isComplete, fetchSetupData } = useSetupSteps();
const showSetupChip = computed(
  () => currentRole.value === 'administrator' && !isComplete.value
);
onMounted(fetchSetupData);

// Ring geometry for the progress chip: r = (size - stroke) / 2.
const RING_CIRCUMFERENCE = 2 * Math.PI * 7;
const ringOffset = computed(
  () =>
    RING_CIRCUMFERENCE -
    (doneCount.value / steps.value.length) * RING_CIRCUMFERENCE
);

const toggleLabel = computed(() =>
  isMini.value ? t('SIDEBAR.EXPAND_SIDEBAR') : t('SIDEBAR.COLLAPSE_SIDEBAR')
);

const sectionCopy = computed(() => ({
  inbox: {
    title: t('RAIL.INBOX'),
    subtitle: t('TOPBAR.SECTIONS.INBOX'),
  },
  ai: {
    title: t('RAIL.AI'),
    subtitle: t('TOPBAR.SECTIONS.AI'),
  },
  reports: {
    title: t('RAIL.REPORTS'),
    subtitle: t('TOPBAR.SECTIONS.REPORTS'),
  },
  customers: {
    title: t('RAIL.CUSTOMERS'),
    subtitle: t('TOPBAR.SECTIONS.CUSTOMERS'),
  },
  team: {
    title: t('RAIL.TEAM'),
    subtitle: t('TOPBAR.SECTIONS.TEAM'),
  },
  travel: {
    title: t('RAIL.TRAVEL'),
    subtitle: t('TOPBAR.SECTIONS.TRAVEL'),
  },
  settings: {
    title: t('RAIL.SETTINGS'),
    subtitle: t('TOPBAR.SECTIONS.SETTINGS'),
  },
}));

const activeSection = computed(() => findRailSectionForRoute(route.name));

// The notification centre lives inside the Inbox rail section, but it is the
// notification list rather than the conversation inbox, so it names itself.
const pageCopy = computed(() => {
  if (isAInboxViewRoute(route.name, true)) {
    return {
      title: t('TOPBAR.NOTIFICATIONS'),
      subtitle: t('TOPBAR.SECTIONS.NOTIFICATIONS'),
    };
  }

  // Campaigns sit under the Customers rail section but are outbound work, not
  // customer records, so the section subtitle describes the wrong page.
  if (String(route.name).startsWith('campaigns')) {
    return {
      title: t('CAMPAIGN.ALL.HEADER_TITLE'),
      subtitle: t('CAMPAIGN.ALL.HEADER_SUBTITLE'),
    };
  }

  if (route.name === 'setup_index') {
    return {
      title: t('SETUP.TITLE'),
      subtitle: t('TOPBAR.SECTIONS.SETUP'),
    };
  }

  return (
    sectionCopy.value[activeSection.value?.name] || {
      title: t('TOPBAR.DEFAULT_TITLE'),
      subtitle: t('TOPBAR.DEFAULT_SUBTITLE'),
    }
  );
});

const openNotifications = () => {
  router.push({ name: 'inbox_view' });
};
</script>

<template>
  <header
    class="relative z-30 flex flex-shrink-0 items-center gap-3 border-b border-n-weak bg-n-background/95 px-3 py-1.5 backdrop-blur md:px-4 shell:h-12 shell:py-0"
  >
    <button
      v-if="hasPanel"
      v-tooltip.bottom-start="toggleLabel"
      type="button"
      :aria-label="toggleLabel"
      :aria-expanded="!isMini"
      class="hidden size-7 flex-shrink-0 place-content-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 outline outline-1 outline-transparent focus-visible:outline-2 focus-visible:outline-n-violet-9 shell:grid"
      @click="toggleMode"
    >
      <span
        class="size-4"
        :class="
          isMini ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'
        "
      />
    </button>

    <div class="flex min-w-0 flex-1 items-baseline gap-2">
      <h1
        class="m-0 truncate font-interDisplay text-sm font-semibold leading-tight tracking-tight text-n-slate-12"
      >
        {{ pageCopy.title }}
      </h1>
      <p
        class="m-0 hidden truncate text-xs leading-tight text-n-slate-10 lg:block"
      >
        {{ pageCopy.subtitle }}
      </p>
    </div>

    <div class="flex flex-shrink-0 items-center gap-1.5 md:gap-2">
      <RouterLink
        v-if="showSetupChip"
        :to="{ name: 'setup_index' }"
        class="hidden h-8 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak transition-colors hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-violet-9 md:flex"
      >
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          class="-rotate-90 shrink-0"
        >
          <circle
            cx="8"
            cy="8"
            r="7"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="text-n-slate-5"
          />
          <circle
            cx="8"
            cy="8"
            r="7"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            class="text-n-violet-9"
            :stroke-dasharray="RING_CIRCUMFERENCE"
            :stroke-dashoffset="ringOffset"
          />
        </svg>
        <span>
          {{ t('SETUP.CHIP', { done: doneCount, total: steps.length }) }}
        </span>
      </RouterLink>

      <!--
        No shortcut chip here: ⌘K belongs to the command bar (ninja-keys' own
        binding), and search has no global key of its own. The keyboard route
        to search is ⌘K then "search", which the command bar's Go to Search
        entry serves - a chip on this row would name a key that does something
        else.
      -->
      <RouterLink
        :to="{ name: 'search' }"
        class="hidden h-8 w-[clamp(12rem,24vw,20rem)] items-center gap-2 rounded-lg bg-n-alpha-1 px-2.5 text-xs text-n-slate-10 outline outline-1 outline-n-weak transition-colors hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-violet-9 lg:flex"
        :aria-label="t('TOPBAR.SEARCH')"
      >
        <span class="i-lucide-search size-4 flex-shrink-0" />
        <span class="truncate">{{ t('TOPBAR.SEARCH') }}</span>
      </RouterLink>

      <RouterLink
        :to="{ name: 'search' }"
        class="hidden size-8 place-content-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-violet-9 sm:grid lg:hidden"
        :aria-label="t('TOPBAR.SEARCH')"
      >
        <span class="i-lucide-search size-4" />
      </RouterLink>

      <SidebarNotificationBell @open-notification-panel="openNotifications" />
    </div>
  </header>
</template>
